import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { PageableTableComponent } from '../../shared/controls/pageable-table/pageable-table.component';
import { TabControlComponent } from '../../shared/controls/tab-control/tab-control.component';
import { TabPageComponent } from '../../shared/controls/tab-control/tab-page.component';
import { SidePanelComponent } from '../../shared/controls/side-panel/side-panel.component';
import { SwitchControlComponent } from '../../shared/controls/switch-control/switch-control.component';

import { InitializerService } from '../../shared/services/initializer-service';
import { ToastService } from '../../shared/toast/toast.service';
import { ErrorService } from '../../shared/services/error-service';

import { PolicyGroupService, CreatePolicyGroupInput, PagingData, PoliciesByGroupIdItem, UpdatePolicyGroupInput, UpdatePoliciesOnGroupInput, AvailablePolicyItem } from '../../../service/proxy';

@Component({
    templateUrl: './create-or-update-policy-group.component.html',
    styleUrls: ['./create-or-update-policy-group.component.scss'],

    imports: [
        CommonModule,
        FormsModule,
        TabControlComponent,
        TabPageComponent,
        PageableTableComponent,
        SidePanelComponent,
        SwitchControlComponent
    ],

    providers: [
        PolicyGroupService
    ]
})
export class CreateOrUpdatePolicyGroupComponent implements OnInit {
    public search: string = '';
    public paging: PagingData = new PagingData();
    public groupPolicies: PoliciesByGroupIdItem[] = [];
    public availablePolicies: AvailablePolicyItem[] = [];

    public policyGroupId: number = 0;
    public policyGroup: any = {};

    public isSaving: boolean = false;
    public isAddVisible: boolean = false;
    public panelCaption: string = 'Manage Policies';

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private policyGroupService: PolicyGroupService,
        private initializerService: InitializerService,
        private toastService: ToastService,
        private errorService: ErrorService)
        {
            this.paging = this.initializerService.createPagingData();
        }

    ngOnInit(): void {
        this.route.paramMap.subscribe((params) => {
            this.policyGroupId = Number(params.get('id'));

            if (this.policyGroupId > 0) {
                this.getPolicyGroupById(this.policyGroupId);
            }
        });
    }

    getPolicyGroupById(policyGroupId: number): void {
        this.policyGroupService.getPolicyGroupById(policyGroupId).subscribe((result) => {
            this.policyGroup = result;
        });
    }

    getColumnMap() {
        return {
            clientName: 'Client Name',
            areaName: 'Area Name',
            policyNumber: 'Policy Number'
        };
    }

    onSave(form: any): void {
        if (!form.valid) {
            return;
        }

        this.isSaving = true;

        if (this.policyGroupId <= 0) {
            this.createPolicyGroup();
        }
        else {
            this.updatePolicyGroup();
        }
    }

    onCancel(): void {
        this.router.navigate(['/policy-group']);
    }

    createPolicyGroup(): void {
        this.isSaving = true;
        const input = this.policyGroup as CreatePolicyGroupInput;

        this.policyGroupService.createPolicyGroup(input)
            .subscribe({
                next: () => {
                    this.toastService.show('Policy Group created successfully.', 'success');
                    this.isSaving = false;

                    setTimeout(() => {
                        this.router.navigate(['/policy-group']);
                    }, 2000);
                },
                error: (error: any) => {
                    this.toastService.show('Failed to create Policy Group.', 'error');
                    this.errorService.handleError(error);
                    this.isSaving = false;
                }
            }
        );
    }

    updatePolicyGroup(): void {
        this.isSaving = true;
        const input = this.policyGroup as UpdatePolicyGroupInput;

        this.policyGroupService.updatePolicyGroup(this.policyGroupId, input)
            .subscribe({
                next: () => {
                    this.toastService.show('Policy Group updated successfully.', 'success');
                    this.isSaving = false;

                    setTimeout(() => {
                        this.router.navigate(['/policy-group']);
                    }, 2000);
                },
                error: (error: any) => {
                    this.toastService.show('Failed to update Policy Group.', 'error');
                    this.errorService.handleError(error);
                    this.isSaving = false;
                }
            }
        );
    }

    getPoliciesForGroup(pagingData: PagingData = new PagingData): void {
        this.policyGroupService.getPoliciesByPolicyGroupId(this.policyGroupId, this.search, pagingData.pageNo, pagingData.pageSize, pagingData.sortDirection, pagingData.sortColumn)
            .subscribe({
                next: (data) => {
                    this.paging = data.pagingData;
                    this.groupPolicies = data.results || [];
                },
                error: (error) => {
                    if (error.status === 204) {
                        this.groupPolicies = [];
                    }
                    else {
                        this.errorService.handleError(error);
                    }
                }
            }
        );
    }

    openAddPolicyDialog(): void {
        this.isAddVisible = true;
        this.getAvailablePolicies();
    }

    getAvailablePolicies(): void {
        this.policyGroupService
            .getAvailablePolicies(
                this.policyGroupId,
                this.search,
            )
            .subscribe((data) => {
                this.availablePolicies = data.results || [];
            }
        );
    }

    savePolicies(): void {
        const selectedPolicies = this.availablePolicies.filter(policy => policy.isSelected);

        const input: UpdatePoliciesOnGroupInput = new UpdatePoliciesOnGroupInput();
        input.policies = selectedPolicies.map(policy => policy.id);

        this.policyGroupService.updatePoliciesOnGroup(this.policyGroupId, input)
            .subscribe({
                next: () => {
                    this.getPoliciesForGroup(this.paging);
                    this.isAddVisible = false;
                },
                error: (error: any) => {
                    this.errorService.handleError(error);
                }
            });
    }

    onSidePanelDone(): void {
        this.savePolicies();
        this.isAddVisible = false;
    }

    onSidePanelCancel(): void {
        this.isAddVisible = false;
    }
}
